import {
  keepPreviousData,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import {
  lazy,
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type MouseEvent,
} from "react";

import { AnimatedBell } from "../components/AnimatedBell";
import { AnimatedSearchField } from "../components/AnimatedSearchField";
import { CurrencySelector } from "../components/CurrencySelector";
import {
  CollapseSidebarIcon,
  ExpandSidebarIcon,
} from "../components/SidebarToggleIcons";
import {
  ActivityNavIcon,
  CompareNavIcon,
  MarketsNavIcon,
  NotificationsNavIcon,
  PortfolioNavIcon,
  StatisticsNavIcon,
  SupportIcon,
} from "../components/NavigationIcons";
import { ComparisonTable } from "../features/comparison/components/ComparisonTable";
import { FilterPanel } from "../features/comparison/components/FilterPanel";
import {
  applyLocalFilters,
  toCompareRequest,
} from "../features/comparison/filters";
import {
  getComparison,
  getSavedOpportunities,
  saveOpportunity,
} from "../features/comparison/api";
import { DEFAULT_FILTERS } from "../features/comparison/filters";
import type {
  ComparisonFilters,
  Opportunity,
  SavedOpportunity,
  SavedOpportunityListResponse,
} from "../features/comparison/types";
import { getOpportunityKey } from "../features/comparison/components/ComparisonTable";
import { getMarkets } from "../features/markets/api";
import { useDebouncedValue } from "../hooks/useDebouncedValue";
import {
  comparisonUrlSearch,
  getComparisonUrlState,
} from "../features/comparison/urlState";
import { useAccount } from "../features/account/useAccount";
import { AuthPage } from "../features/account/components/AuthPage";
import { LegalPage } from "../features/legal/LegalPage";

const AccountCenter = lazy(() =>
  import("../features/account/components/AccountCenter").then((module) => ({
    default: module.AccountCenter,
  })),
);

type WorkspaceSection = "comparison" | "deals" | "alerts";

const SECTION_PATHS: Record<WorkspaceSection, string> = {
  comparison: "/table",
  deals: "/deals",
  alerts: "/alerts",
};

const AUTH_URL = "https://neluminar.com/auth";

function sectionFromPathname(pathname: string): WorkspaceSection {
  const normalized = pathname.replace(/\/+$/, "") || "/";
  if (normalized === SECTION_PATHS.deals) {
    return "deals";
  }
  if (normalized === SECTION_PATHS.alerts) {
    return "alerts";
  }
  return "comparison";
}

function workspaceUrl(
  section: WorkspaceSection,
  filters: ComparisonFilters,
  searchQuery: string,
): string {
  if (section === "comparison") {
    return `${SECTION_PATHS.comparison}${comparisonUrlSearch(filters, searchQuery)}`;
  }

  const params = new URLSearchParams();
  if (searchQuery.trim()) {
    params.set("q", searchQuery.trim());
  }
  const search = params.toString();
  return `${SECTION_PATHS[section]}${search ? `?${search}` : ""}`;
}

const navigation: Array<{
  id?: WorkspaceSection;
  label: string;
  icon: typeof CompareNavIcon;
}> = [
  { id: "comparison", label: "Сравнение", icon: CompareNavIcon },
  { id: "deals", label: "Сделки", icon: ActivityNavIcon },
  { id: "alerts", label: "Оповещения", icon: NotificationsNavIcon },
  { label: "Портфель", icon: PortfolioNavIcon },
  { label: "Статистика", icon: StatisticsNavIcon },
  { label: "Маркеты", icon: MarketsNavIcon },
];

function SavedItemsPanel({ section }: { section: "deals" | "alerts" }) {
  return (
    <aside className="filters-panel saved-items-panel" aria-label="Параметры">
      <div className="filters-heading">
        <div>
          <span>СОХРАНЕННОЕ</span>
          <h2>{section === "deals" ? "Сделки" : "Оповещения"}</h2>
        </div>
        <span className="refresh-state" aria-hidden="true" />
      </div>
    </aside>
  );
}

export function App() {
  if (typeof window !== "undefined" && window.location.pathname.startsWith("/auth")) {
    return <AuthPage />;
  }
  if (typeof window !== "undefined" && window.location.pathname.startsWith("/legal")) {
    return <LegalPage />;
  }
  const { account, updatePreferences } = useAccount();
  const queryClient = useQueryClient();
  const [activeSection, setActiveSection] = useState<WorkspaceSection>(() =>
    typeof window === "undefined"
      ? "comparison"
      : sectionFromPathname(window.location.pathname),
  );
  const [initialUrlState] = useState(getComparisonUrlState);
  const [filters, setFilters] = useState<ComparisonFilters>(
    initialUrlState.filters,
  );
  const [searchQuery, setSearchQuery] = useState(
    initialUrlState.searchQuery,
  );
  const [isAccountOpen, setIsAccountOpen] = useState(() => {
    const action = new URLSearchParams(window.location.search).get("auth");
    return action === "verify-email" || action === "reset-password";
  });
  const shellRef = useRef<HTMLDivElement>(null);
  const toggleSidebar = (event: MouseEvent<HTMLButtonElement>) => {
    const shell = shellRef.current;
    if (!shell) {
      return;
    }

    const workspace = shell.querySelector<HTMLElement>(".workspace");
    workspace?.getAnimations().forEach((animation) => animation.cancel());
    const previousRect = workspace?.getBoundingClientRect();
    const collapsed = shell.classList.toggle("is-sidebar-collapsed");
    if (account) {
      void updatePreferences({ sidebar_collapsed: collapsed }).catch(
        () => undefined,
      );
    }

    if (
      workspace &&
      previousRect &&
      !window.matchMedia("(prefers-reduced-motion: reduce)").matches
    ) {
      const nextRect = workspace.getBoundingClientRect();
      const scaleX =
        nextRect.width > 0 ? previousRect.width / nextRect.width : 1;
      const translateX = previousRect.right - nextRect.right;
      const animation = workspace.animate(
        [
          {
            transform: `translateX(${translateX}px) scaleX(${scaleX})`,
            transformOrigin: "right center",
          },
          {
            transform: "translateX(0) scaleX(1)",
            transformOrigin: "right center",
          },
        ],
        {
          duration: 230,
          easing: "cubic-bezier(0.22, 1, 0.36, 1)",
        },
      );
      void animation.finished
        .then(() => animation.cancel())
        .catch(() => undefined);
    }

    event.currentTarget.setAttribute("aria-expanded", String(!collapsed));
    event.currentTarget.setAttribute(
      "title",
      collapsed ? "Развернуть меню" : "Свернуть меню",
    );
  };
  const debouncedFilters = useDebouncedValue(filters, 280);
  const compareRequest = useMemo(
    () => toCompareRequest(debouncedFilters),
    [debouncedFilters],
  );
  const marketsQuery = useQuery({
    queryKey: ["markets"],
    queryFn: ({ signal }) => getMarkets(signal),
    retry: 1,
    staleTime: 60_000,
  });
  const savedQueryKey = [
    "saved-opportunities",
    account?.user.public_id,
  ] as const;
  const savedQuery = useQuery({
    queryKey: savedQueryKey,
    queryFn: ({ signal }) => getSavedOpportunities(signal),
    enabled: Boolean(account),
    retry: false,
    staleTime: 30_000,
  });
  const saveMutation = useMutation({
    mutationFn: saveOpportunity,
    onMutate: async (values) => {
      const previous = queryClient.getQueryData<SavedOpportunityListResponse>(
        savedQueryKey,
      );

      if (previous) {
        const existing = previous.rows.find(
          (row) => row.opportunity_key === values.opportunity_key,
        );
        const rows = previous.rows.filter(
          (row) => row.opportunity_key !== values.opportunity_key,
        );
        if (values.is_deal || values.is_alert) {
          rows.push(
            existing ?? {
              id: -Date.now(),
              opportunity_key: values.opportunity_key,
              skin_id: values.skin_id,
              buy_market: values.buy_market,
              sell_market: values.sell_market,
              is_deal: values.is_deal,
              is_alert: values.is_alert,
              snapshot: values.snapshot as Opportunity,
              created_at: new Date().toISOString(),
              updated_at: new Date().toISOString(),
            },
          );
          if (existing) {
            rows[rows.length - 1] = {
              ...existing,
              is_deal: values.is_deal,
              is_alert: values.is_alert,
              snapshot: values.snapshot as Opportunity,
              updated_at: new Date().toISOString(),
            };
          }
        }
        queryClient.setQueryData(savedQueryKey, { ok: true, rows });
      }

      await queryClient.cancelQueries({ queryKey: savedQueryKey });
      return { previous };
    },
    onError: (_error, _values, context) => {
      if (context?.previous) {
        queryClient.setQueryData(savedQueryKey, context.previous);
      }
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: ["saved-opportunities"] });
    },
  });
  const comparisonQuery = useInfiniteQuery({
    queryKey: ["comparison", compareRequest],
    queryFn: ({ pageParam, signal }) =>
      getComparison(
        {
          ...compareRequest,
          offset: pageParam,
        },
        signal,
      ),
    initialPageParam: 0,
    getNextPageParam: (lastPage) =>
      lastPage.summary.has_more
        ? (lastPage.summary.next_offset ?? undefined)
        : undefined,
    placeholderData: keepPreviousData,
    retry: 1,
    staleTime: 15_000,
    refetchOnWindowFocus: false,
  });
  const markets = useMemo(
    () => marketsQuery.data?.rows ?? [],
    [marketsQuery.data?.rows],
  );
  const marketIcons = useMemo(
    () => new Map(markets.map((market) => [market.code, market.icon_url])),
    [markets],
  );
  const pagedRows = useMemo(() => {
    const uniqueRows = new Map<string, Opportunity>();
    for (const page of comparisonQuery.data?.pages ?? []) {
      for (const row of page.rows) {
        const key = `${row.skin_id}:${row.buy_market}:${row.sell_market}`;
        if (!uniqueRows.has(key)) {
          uniqueRows.set(key, row);
        }
      }
    }
    return [...uniqueRows.values()];
  }, [comparisonQuery.data?.pages]);
  const rows = useMemo(
    () =>
      applyLocalFilters(
        pagedRows.map((row) => ({
          ...row,
          buy_market_icon_url:
            row.buy_market_icon_url ?? marketIcons.get(row.buy_market) ?? null,
          sell_market_icon_url:
            row.sell_market_icon_url ?? marketIcons.get(row.sell_market) ?? null,
        })),
        filters,
        searchQuery,
      ),
    [pagedRows, filters, marketIcons, searchQuery],
  );
  const savedByKey = useMemo(() => {
    const result = new Map<string, SavedOpportunity>();
    for (const saved of savedQuery.data?.rows ?? []) {
      result.set(saved.opportunity_key, saved);
    }
    return result;
  }, [savedQuery.data?.rows]);
  const savedRows = useMemo(
    () =>
      (savedQuery.data?.rows ?? [])
        .filter((saved) =>
          activeSection === "deals" ? saved.is_deal : saved.is_alert,
        )
        .map((saved) => saved.snapshot),
    [activeSection, savedQuery.data?.rows],
  );
  const isSavedSection = activeSection !== "comparison";
  const displayFilters = isSavedSection ? DEFAULT_FILTERS : filters;
  const displayRows = useMemo(
    () =>
      applyLocalFilters(
        isSavedSection ? savedRows : rows,
        displayFilters,
        searchQuery,
      ),
    [displayFilters, isSavedSection, rows, savedRows, searchQuery],
  );
  const openAccount = useCallback(() => {
    if (account) {
      setIsAccountOpen(true);
      return;
    }
    window.location.assign(AUTH_URL);
  }, [account]);
  const handleSaveOpportunity = useCallback(
    (opportunity: Opportunity, kind: "deal" | "alert") => {
      if (!account) {
        openAccount();
        return;
      }
      const opportunityKey = getOpportunityKey(opportunity);
      const current = savedByKey.get(opportunityKey);
      saveMutation.mutate({
        opportunity_key: opportunityKey,
        skin_id: opportunity.skin_id,
        buy_market: opportunity.buy_market,
        sell_market: opportunity.sell_market,
        is_deal:
          kind === "deal" ? !current?.is_deal : Boolean(current?.is_deal),
        is_alert:
          kind === "alert" ? !current?.is_alert : Boolean(current?.is_alert),
        snapshot: opportunity,
      });
    },
    [account, openAccount, saveMutation, savedByKey],
  );
  const navigateToSection = useCallback(
    (section: WorkspaceSection) => {
      const nextUrl = workspaceUrl(section, filters, searchQuery);
      const currentUrl = `${window.location.pathname}${window.location.search}`;
      if (nextUrl !== currentUrl) {
        window.history.pushState(null, "", nextUrl);
      }
      setActiveSection(section);
    },
    [filters, searchQuery],
  );
  useEffect(() => {
    const syncFromHistory = () => {
      const state = getComparisonUrlState();
      setActiveSection(sectionFromPathname(window.location.pathname));
      setFilters(state.filters);
      setSearchQuery(state.searchQuery);
    };

    window.addEventListener("popstate", syncFromHistory);
    return () => window.removeEventListener("popstate", syncFromHistory);
  }, []);
  useEffect(() => {
    const nextUrl = `${workspaceUrl(activeSection, filters, searchQuery)}${window.location.hash}`;
    const currentUrl = `${window.location.pathname}${window.location.search}${window.location.hash}`;
    if (nextUrl !== currentUrl) {
      window.history.replaceState(null, "", nextUrl);
    }
  }, [activeSection, filters, searchQuery]);
  const errorMessage = isSavedSection
    ? savedQuery.error instanceof Error
      ? savedQuery.error.message
      : null
    : comparisonQuery.error instanceof Error
      ? comparisonQuery.error.message
      : null;
  const savedSidebarCollapsed = account?.preferences.sidebar_collapsed;
  const profileName = account?.user.display_name || "Гость";
  const profileInitials =
    profileName
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((part) => part.slice(0, 1).toUpperCase())
      .join("") || "NT";

  useEffect(() => {
    const shell = shellRef.current;
    if (!shell || savedSidebarCollapsed === undefined) {
      return;
    }
    shell.classList.toggle("is-sidebar-collapsed", savedSidebarCollapsed);
    const toggle = shell.querySelector<HTMLButtonElement>(".sidebar-toggle");
    toggle?.setAttribute("aria-expanded", String(!savedSidebarCollapsed));
  }, [savedSidebarCollapsed]);

  return (
    <div className="app-shell" ref={shellRef}>
      <aside className="sidebar">
        <div className="brand">
          <img src="/neluminar-logo.svg" alt="" />
          <span>NeLuminar</span>
        </div>

        <nav aria-label="Основная навигация">
          {navigation.map(({ id, label, icon: Icon }) => (
            <button
              className={`nav-item${id && activeSection === id ? " is-active" : ""}`}
              type="button"
              key={label}
              aria-current={id && activeSection === id ? "page" : undefined}
              title={label}
              onClick={() => {
                if (id) {
                  navigateToSection(id);
                }
              }}
            >
              <Icon aria-hidden="true" />
              <span>{label}</span>
            </button>
          ))}
        </nav>

        <div className="sidebar-bottom">
          <button
            className="sidebar-toggle"
            type="button"
            title="Свернуть меню"
            aria-label="Свернуть или развернуть меню"
            aria-expanded="true"
            onClick={toggleSidebar}
          >
            <ExpandSidebarIcon className="sidebar-open-icon" />
            <CollapseSidebarIcon className="sidebar-close-icon" />
            <span>Свернуть</span>
          </button>
          <button className="support-button" type="button">
            <SupportIcon aria-hidden="true" />
            <span>Поддержка</span>
          </button>
          <small>© NeLuminar Team</small>
        </div>
      </aside>

      <header className="workspace-header">
        <div className="workspace-heading">
          <AnimatedSearchField
            value={searchQuery}
            onValueChange={setSearchQuery}
          />
        </div>
        <div className="workspace-actions">
          <CurrencySelector />
          <button
            className="notification-button"
            type="button"
            title="Уведомления"
            aria-label="Уведомления"
          >
            <AnimatedBell />
          </button>
          <button
            className="header-profile"
            type="button"
            aria-haspopup="dialog"
            onClick={openAccount}
          >
            <span className="profile-copy">
              <strong>{profileName}</strong>
              <small>Профиль</small>
            </span>
            {account?.profile.avatar_url ? (
              <img
                className="profile-avatar"
                src={account.profile.avatar_url}
                alt=""
              />
            ) : (
              <span className="profile-avatar" aria-hidden="true">
                {profileInitials}
              </span>
            )}
          </button>
        </div>
      </header>

      <main className="workspace">
        <ComparisonTable
          rows={displayRows}
          totalMatches={
            isSavedSection
              ? displayRows.length
              : comparisonQuery.data?.pages[0]?.summary.matches_found ?? 0
          }
          isInitialLoading={
            isSavedSection
              ? Boolean(account) && savedQuery.isPending
              : comparisonQuery.isPending && comparisonQuery.data === undefined
          }
          isRefreshing={
            isSavedSection
              ? Boolean(account) && savedQuery.isFetching
              : comparisonQuery.isFetching && !comparisonQuery.isFetchingNextPage
          }
          hasMore={
            !isSavedSection &&
            Boolean(comparisonQuery.hasNextPage) &&
            !comparisonQuery.isPlaceholderData
          }
          isLoadingMore={!isSavedSection && comparisonQuery.isFetchingNextPage}
          loadingRowCount={compareRequest.limit}
          onLoadMore={() => {
            if (!isSavedSection) {
              void comparisonQuery.fetchNextPage();
            }
          }}
          errorMessage={errorMessage}
          filters={displayFilters}
          markets={markets}
          searchQuery={searchQuery}
          onFiltersChange={setFilters}
          onSearchQueryChange={setSearchQuery}
          savedByKey={savedByKey}
          onSaveOpportunity={handleSaveOpportunity}
        />
      </main>

      {activeSection === "deals" || activeSection === "alerts" ? (
        <SavedItemsPanel section={activeSection} />
      ) : (
        <FilterPanel
          filters={filters}
          markets={markets}
          onChange={setFilters}
          onRequestAccount={openAccount}
        />
      )}
      {isAccountOpen ? (
        <Suspense fallback={null}>
          <AccountCenter
            open
            onClose={() => setIsAccountOpen(false)}
          />
        </Suspense>
      ) : null}
    </div>
  );
}
